import PropTypes from 'prop-types';
import * as fields from '@anu/utilities/fields';
import { transformLesson } from '@anu/utilities/transform.lesson';
import { transformQuiz } from '@anu/utilities/transform.quiz';
import {
  transformCourseCategory,
  courseCategoryPropTypes,
} from '@anu/utilities/transform.courseCategory';
import { transformCourseTopic, courseTopicPropTypes } from '@anu/utilities/transform.courseTopic';
import {
  calculateProgressPercent,
  prepareCourseProgress,
} from '@anu/utilities/progress';
import { transformParagraph } from '@anu/utilities/transform.paragraphs';

/**
 * Extends original `transform.course` by introducing `private_sessions` prop
 * to the `course`.
 */

/**
 * Transform course node from Drupal backend
 * into frontend-friendly object.
 */
const transformCourse = (node, data) => {
  // Make sure the course node data exists.
  if (!fields.getNumberValue(node, 'nid')) {
    return null;
  }

  const courseId = fields.getNumberValue(node, 'nid');
  // Prepare courses pages for current course.
  let coursesPageUrls = [];
  if (data.courses_page_urls_by_course) {
    data.courses_page_urls_by_course.map((item) => {
      if (item.course_id === courseId) {
        coursesPageUrls = item.courses_page_urls;
      }
    });
  }

  // Prepare first lesson url for current course.
  let firstLessonUrl = '';
  if (data.first_lesson_url_by_course) {
    data.first_lesson_url_by_course.map((set) => {
      if (set.course_id === courseId) {
        firstLessonUrl = set.first_lesson_url;
      }
    });
  }

  const track_progress = fields.getBooleanValue(node, 'field_course_track_progress');
  const linear_progress = fields.getBooleanValue(node, 'field_course_track_progress');
  const progress = track_progress || linear_progress
    ? prepareCourseProgress(node)
    : null;
  const progress_percent = calculateProgressPercent(progress);

  const transform = {
    id: courseId,
    title: fields.getTextValue(node, 'title'),
    description: fields.getTextValue(node, 'field_course_description'),
    url: fields.getNodeUrl(node),
    image: fields.getImage(node, 'field_course_image', 'course_preview'),
    categories: fields
      .getArrayValue(node, 'field_course_category')
      .map((term) => transformCourseCategory(term)),
    topics: fields
      .getArrayValue(node, 'field_course_topics')
      .map((term) => transformCourseTopic(term)),
    labels: fields
      .getArrayValue(node, 'field_course_label')
      .map((term) => fields.getTextValue(term, 'name')),
    content: fields.getArrayValue(node, 'field_course_module').map((module) => ({
      module: fields.getTextValue(module, 'field_module_title'),
      lessons: fields
        .getArrayValue(module, 'field_module_lessons')
        .map((lesson) => transformLesson(lesson, {
          id: courseId,
          progress,
          track_progress,
          linear_progress
        }))
        .filter((lesson) => !!lesson),
      quiz: transformQuiz(fields.getArrayValue(module, 'field_module_assessment')[0], data, {
        id: courseId,
        progress,
      }),
    })),
    courses_page_urls: coursesPageUrls,
    first_lesson_url: firstLessonUrl,
    track_progress,
    linear_progress,
    progress,
    progress_percent,
    // TODO: add support for offline unlocking of courses in categories.
    locked: fields.getBooleanValue(node, 'locked'),
    audios: fields.getArrayValue(node, 'audios'),
    content_urls: fields.getArrayValue(node, 'content_urls'),
    lessons_count: node.lessons_count,
    private_sessions: fields.getBooleanValue(node, 'field_course_private_sessions'),
    summary: {
      enabled: fields.getBooleanValue(node, 'field_course_summary'),
      title: fields.getTextValue(node, 'field_course_summary_title'),
      unanswered_alert: fields.getTextValue(node, 'field_course_summary_unanswered'),
      header: fields
        .getArrayValue(node, 'field_course_summary_header')
        .map((paragraph) => transformParagraph(paragraph))
        .filter((paragraph) => !!paragraph),
      footer: fields
        .getArrayValue(node, 'field_course_summary_footer')
        .map((paragraph) => transformParagraph(paragraph))
        .filter((paragraph) => !!paragraph),
    },
    questions: data.course && data.course.questions ? data.course.questions : [],
  };

  // Include the summary in the last module if it is enabled.
  if (transform.summary.enabled) {
    transform.content[transform.content.length - 1].lessons.push({
      ...transform.summary,
      // Add properties required for navigation.
      id: -1,
      url: transform.url + '/summary',
      sections: [[]],
      isCompleted: progress_percent === 100,
    });
  }

  // Redirect to summary if course is completed.
  if (progress_percent === 100 && transform.summary.enabled) {
    return {
      ...transform,
      first_lesson_url: transform.url + '/summary',
    };
  }

  // Redirect to the first incomplete lesson.
  if (progress) {
    let currentLesson = Object.values(progress).find((lesson) => lesson.prev === 0);
    while (currentLesson.next && currentLesson.completed) {
      currentLesson = progress[currentLesson.next];
    }

    return {
      ...transform,
      first_lesson_url: currentLesson.url,
    };
  }

  return transform;
};

/**
 * Define expected prop types for course.
 */
const coursePropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  description: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  categories: PropTypes.arrayOf(courseCategoryPropTypes),
  topics: PropTypes.arrayOf(courseTopicPropTypes),
  content: PropTypes.arrayOf(
    PropTypes.shape({
      module: PropTypes.string,
      lessons: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
  labels: PropTypes.arrayOf(PropTypes.string),
  courses_pages: PropTypes.arrayOf(PropTypes.shape({})),
  first_lesson_url: PropTypes.string,
  progress: PropTypes.shape({}),
  track_progress: PropTypes.bool,
  linear_progress: PropTypes.bool,
  progress_percent: PropTypes.number.isRequired,
  locked: PropTypes.bool,
  audios: PropTypes.arrayOf(PropTypes.string),
  content_urls: PropTypes.arrayOf(PropTypes.string),
  lessons_count: PropTypes.number,
  private_sessions: PropTypes.bool,
  summary: PropTypes.shape({
    enabled: PropTypes.bool,
    title: PropTypes.string.isRequired,
    unanswered_alert: PropTypes.string,
    header: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        bundle: PropTypes.string.isRequired,
      })
    ),
    footer: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        bundle: PropTypes.string.isRequired,
      })
    ),
    questions: PropTypes.array,
  }),
});

export { transformCourse, coursePropTypes };
